/**
 * 「TA 的小动作」里的基础动作（UI2-BASIC-MOTION-ROWS-117；c84a 10-16 12:54，I 转；I 10-17 答 Q1、Q2）：
 * - 一张卡「基础动作」，接在「已学会」后面：卡头「已经会 X/14 个」，按类折叠（组头写这一类会了几段），点开逐段一行，每段照实写会了、在学、暂时做不好。
 *   全部摊开有 36 行太长，所以按类折叠；点开就是每段一行（Q1）。原生 details，键盘、读屏都现成。
 * - 卡底小字：一共几个、已经会几个（照 overall）；有暂时做不好的说有几个，不说原因；换形象过渡期说一句先用原来的样子做。
 * - 教学单「准备中」那一组里只多一行「TA 还在学 N 个基础动作」，点了跳到这张卡、展开第一组还在学的，不在那一组里逐段重复（Q2）。
 * - 说法见 ./basicMotions：不写准备、制作、生成；不写倒计时、预计时间，不画一直在转的东西。数据没有就整块不出。
 */
import { useEffect, useRef } from "react";
import { Icon } from "@/shared/ui";
import {
  BASIC_TITLE, BASIC_TRANSITION, STATUS_LABEL, basicGroupLine, basicHeadLine, basicLearningRow, basicOverallLine, basicUnavailableLine, clipLabel,
  groupBasicMotions, inTransition, unavailableCount, type BasicMotions,
} from "./basicMotions";

export const BASIC_CARD_ID = "teach-basic";

/** 从「准备中」那一行跳过来：要展开哪一类（seq 每点一次加一，同一类再点也会重新跳） */
export interface BasicJump {
  category: string;
  seq: number;
}

export function TeachBasicMotions({ motions, jump }: { motions: BasicMotions; jump: BasicJump | null }) {
  const groups = groupBasicMotions(motions.clips);
  const unavailable = unavailableCount(motions);
  const cardRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!jump) return;
    const details = [...(cardRef.current?.querySelectorAll<HTMLDetailsElement>("details[data-category]") ?? [])].find((d) => d.dataset.category === jump.category);
    if (!details) return;
    details.open = true;
    details.scrollIntoView?.({ block: "start" });
    details.querySelector<HTMLElement>("summary")?.focus({ preventScroll: true });
  }, [jump]);

  return (
    <section ref={cardRef} id={BASIC_CARD_ID} className="ps-teach-card ps-teach-group ps-teach-basic" aria-labelledby="teach-basic-title" data-teach-basic>
      <h3 id="teach-basic-title" className="ps-teach-group__title">
        <Icon name="walk" size={18} className="ps-teach-group__icon" />
        <span>{BASIC_TITLE}</span>
      </h3>
      <p className="ps-teach-basic__head" data-basic-head>{basicHeadLine(motions.first_batch)}</p>
      <ul className="ps-teach-basic__groups">
        {groups.map((group) => (
          <li key={group.category}>
            <details className="ps-teach-basic__group" data-category={group.category}>
              <summary className="ps-teach-basic__summary">
                <span className="ps-teach-basic__name">{group.title}</span>
                <span className="ps-teach-basic__count">{basicGroupLine(group.usable, group.total)}</span>
                <Icon name="chevron" size={16} className="ps-teach-basic__chevron" />
              </summary>
              <ul className="ps-teach-basic__clips">
                {group.clips.map((clip) => (
                  <li key={clip.clip_key} className="ps-teach-basic__clip" data-status={clip.status}>
                    <span className="ps-teach-basic__clip-name">{clipLabel(clip)}</span>
                    <span className="ps-teach-basic__status">{STATUS_LABEL[clip.status]}</span>
                  </li>
                ))}
              </ul>
            </details>
          </li>
        ))}
      </ul>
      <div className="ps-teach-basic__foot">
        <p>{basicOverallLine(motions.overall)}</p>
        {unavailable > 0 ? <p data-basic-unavailable>{basicUnavailableLine(unavailable)}</p> : null}
        {inTransition(motions) ? <p data-basic-transition>{BASIC_TRANSITION}</p> : null}
      </div>
    </section>
  );
}

/** 「准备中」那一组里的一行：点了跳到基础动作卡，展开第一组还在学的 */
export function BasicLearningRow({ count, onJump }: { count: number; onJump: () => void }) {
  return (
    <li>
      <button type="button" className="ps-teach-row ps-teach-row--basic" aria-controls={BASIC_CARD_ID} onClick={onJump} data-basic-learning>
        <span className="ps-teach-row__text">
          <span className="ps-teach-row__title">{basicLearningRow(count)}</span>
        </span>
        <Icon name="chevron" size={16} className="ps-teach-row__go" />
      </button>
    </li>
  );
}

/** 第一组还有在学的段（跳过去展开它）；没有就展开第一组 */
export function firstLearningCategory(motions: BasicMotions): string | null {
  const groups = groupBasicMotions(motions.clips);
  return (groups.find((group) => group.learning > 0) ?? groups[0])?.category ?? null;
}
